import React from "react";
import { SiteSearchPaginationBarProps } from "../ArticleProps.interface";
import { usePagination, useInstantSearch } from "react-instantsearch-hooks-web";
import { valueExists } from "../../../../utils/valueExists/valueExists.util";
import isEmptyString from "../../../../utils/isEmptyString/isEmptyString.util";
import classNames from "classnames";

// Pagination Button Subcomponent
interface PaginationButtonProps {
  label: string;
  onClick: () => void;
  symbol: string;
  disabled?: boolean;
  isCurrent?: boolean;
}

const PaginationButton: React.FC<PaginationButtonProps> = ({
  label,
  onClick,
  symbol,
  disabled = false,
  isCurrent = false,
}) => {
  const buttonClasses = classNames(
    "w-10 h-10 rounded-full flex items-center justify-center border text-sm font-medium transition-colors",
    {
      "bg-Primary-900 text-White": isCurrent,
      "bg-white text-Primary-900 border-Body-Disabled hover:bg-Primary-100": !isCurrent && !disabled,
      "cursor-not-allowed opacity-50": disabled,
    },
  );

  return (
    <button
      className={buttonClasses}
      onClick={onClick}
      aria-label={label}
      aria-current={isCurrent ? "page" : undefined}
      disabled={disabled}
    >
      {symbol}
    </button>
  );
};

// Custom Accessible Pagination
const CustomPagination: React.FC = () => {
  const { pages, currentRefinement, isFirstPage, isLastPage, refine } = usePagination();

  const {
    results: { nbPages },
  } = useInstantSearch();

  return (
    <nav className="flex flex-wrap justify-center items-center gap-2" aria-label="Pagination">
      <PaginationButton label="First page" onClick={() => refine(0)} disabled={isFirstPage} symbol="<<" />
      <PaginationButton
        label="Previous page"
        onClick={() => refine(currentRefinement - 1)}
        disabled={isFirstPage}
        symbol="<"
      />
      {pages.map(page => (
        <PaginationButton
          key={page}
          label={`Page ${page + 1}`}
          onClick={() => refine(page)}
          isCurrent={page === currentRefinement}
          symbol={(page + 1).toString()}
        />
      ))}
      <PaginationButton
        label="Next page"
        onClick={() => refine(currentRefinement + 1)}
        disabled={isLastPage}
        symbol=">"
      />
      <PaginationButton label="Last page" onClick={() => refine(nbPages - 1)} disabled={isLastPage} symbol=">>" />
    </nav>
  );
};

// Full SiteSearchPaginationBar Component
const SiteSearchPaginationBar: React.FC<SiteSearchPaginationBarProps> = ({ max_results_per_page, summary_text }) => {
  const INDEX_OF_FIRST_RESULT_ON_PAGE_PLACEHOLDER = "<index-of-first-result-on-page>";
  const INDEX_OF_LAST_RESULT_ON_PAGE_PLACEHOLDER = "<index-of-last-result-on-page>";
  const NUMBER_OF_RESULTS_FOUND_PLACEHOLDER = "<number-of-results-found>";

  const {
    results: { page, nbHits, hits },
  } = useInstantSearch();

  const isSummaryTextRequired = (): boolean => {
    return valueExists(summary_text) && !isEmptyString(summary_text);
  };

  const getFormattedSummaryText = (): JSX.Element => {
    const summaryTextSplitIntoIndividualWords = summary_text.split(" ");
    return (
      <p className="text-center text-Primary-900">
        {summaryTextSplitIntoIndividualWords.map((word, i) =>
          isDynamicNumberPortionRequired(word) ? (
            <span className="font-bold" key={i}>
              {word === INDEX_OF_FIRST_RESULT_ON_PAGE_PLACEHOLDER && `${getIndexOfFirstResultOnPage()} `}
              {word === INDEX_OF_LAST_RESULT_ON_PAGE_PLACEHOLDER && `${getIndexOfLastResultOnPage()} `}
              {word === NUMBER_OF_RESULTS_FOUND_PLACEHOLDER && `${nbHits} `}
            </span>
          ) : (
            <span key={i}>{word} </span>
          ),
        )}
      </p>
    );
  };

  const isDynamicNumberPortionRequired = (word: string): boolean => {
    const dynamicNumberPortions = [
      INDEX_OF_FIRST_RESULT_ON_PAGE_PLACEHOLDER,
      INDEX_OF_LAST_RESULT_ON_PAGE_PLACEHOLDER,
      NUMBER_OF_RESULTS_FOUND_PLACEHOLDER,
    ];
    return dynamicNumberPortions.includes(word);
  };

  const getIndexOfFirstResultOnPage = (): number => {
    return max_results_per_page * page + 1;
  };

  const getIndexOfLastResultOnPage = (): number => {
    return getIndexOfFirstResultOnPage() - 1 + hits.length;
  };

  return (
    <div className="container mx-auto grid grid-cols-2 gap-x-6 md:grid-cols-12">
      <div className="col-span-2 space-y-4 flex flex-col items-center justify-center md:col-span-8 md:col-start-3">
        <CustomPagination />
        {isSummaryTextRequired() && getFormattedSummaryText()}
      </div>
    </div>
  );
};

export default SiteSearchPaginationBar;
